import { AlertTriangle, RefreshCw, Sparkles } from "lucide-react";
import React, { useMemo } from "react";
import { isRouteErrorResponse, useNavigate, useParams, useRouteError } from "react-router-dom";
import { Button, Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle, RmBodyZone, useChat } from "randmarcomps";
import RmPageHeader from "../components/layout/RmPageHeader";
import Path from "../routes/Path";

const DocumentError: React.FC = () => {
  const navigate = useNavigate();
  const { applicationId, documentNumber } = useParams<{ applicationId: string; documentNumber: string }>();
  const routeError = useRouteError();
  const { sendMessageWithClear } = useChat();

  const errorMessage = useMemo(() => {
    if (isRouteErrorResponse(routeError)) {
      return `${routeError.status} ${routeError.statusText}`;
    }
    if (routeError instanceof Error) {
      return routeError.message;
    }
    return "We hit an unexpected issue loading this document.";
  }, [routeError]);

  const assistantPrompt = useMemo(() => {
    const appId = applicationId || "Unknown";
    const docNum = documentNumber || "Unknown";
    return [
      "You are assisting with the Randmar Manufacturer Portal Document page.",
      `Manufacturer applicationId: ${appId}`,
      `DocumentNumber: ${docNum}`,
      "",
      "The Document route hit an unexpected error while rendering.",
      `Error: ${errorMessage}`,
      "",
      "Help me understand likely causes and what to try next.",
    ].join("\n");
  }, [applicationId, documentNumber, errorMessage]);

  return (
    <>
      <RmPageHeader
        title="Document Error"
        subtitle="An issue occurred while rendering this document."
      />
      <RmBodyZone className="pb-10">
        <div className="flex min-h-[60vh] items-center justify-center px-4">
          <Card className="w-full max-w-xl border border-[var(--ops-border)] shadow-[var(--shadow-card)]">
            <CardHeader className="space-y-2">
              <div className="inline-flex items-center gap-2 rounded-full bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-800">
                <AlertTriangle className="h-4 w-4" aria-hidden="true" />
                Document page error
              </div>
              <CardTitle>We couldn&apos;t load this document</CardTitle>
              <CardDescription>
                Something went wrong while rendering the Document page{documentNumber ? ` for ${documentNumber}` : ""}. You can retry, head back to search, or ask the assistant.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="rounded-lg bg-slate-50 p-3 text-sm text-slate-700">
                <p className="font-semibold text-slate-800">What happened</p>
                <p className="mt-1">{errorMessage}</p>
              </div>
            </CardContent>
            <CardFooter className="flex flex-wrap gap-3">
              <Button variant="outline" onClick={() => navigate(-1)}>
                Back
              </Button>
              <Button variant="outline" onClick={() => navigate(Path.search(applicationId || ""))}>
                Go to search
              </Button>
              <Button variant="outline" size="icon" aria-label="Open assistant with this error context" onClick={() => sendMessageWithClear(assistantPrompt)}>
                <Sparkles className="h-4 w-4" aria-hidden="true" />
              </Button>
              <Button onClick={() => navigate(0)}>
                <RefreshCw className="mr-2 h-4 w-4" />
                Try again
              </Button>
            </CardFooter>
          </Card>
        </div>
      </RmBodyZone>
    </>
  );
};

export default DocumentError;
